import React from 'react';
import { useInView } from '../hooks/useInView';

const mbrFeatures = [
  { icon: '▤', title: 'No-code authoring', desc: 'Header, process steps, critical parameters, materials, specs & limits — built without code.', color: '#7B3FE4' },
  { icon: '✨', title: 'AI vision intake', desc: 'Scanned, typeset & legacy PDFs become structured MBR/BPR rows, ready to author and version.', color: '#00C9B1', badge: 'AI' },
  { icon: '↻', title: 'Governed lifecycle', desc: 'Draft → approve → effective → retire — fully versioned, with an obsolete-version block.', color: '#4C6EF5' },
  { icon: '⛔', title: 'Issue control', desc: 'Only an APPROVED, in-effect MBR can issue a batch record.', color: '#E74C3C' },
  { icon: '⧉', title: 'Reusable libraries', desc: 'Clone templates and reuse step & parameter libraries to author the next product faster.', color: '#00C9B1' },
  { icon: '⚙', title: 'Per-product config', desc: 'Templates, limits, gates, and workflow tuned per product and per site.', color: '#F5A623', badge: 'Configurable' },
];

const foresightFeatures = [
  { icon: '≈', title: 'Trend & out-of-trend', desc: 'Predictive trending flags drift before it breaks a spec — batch over batch.', color: '#4C6EF5' },
  { icon: '◰', title: 'SPC, uni & multivariate', desc: 'Control charts and multivariate models catch shifts a single parameter would hide.', color: '#7B3FE4' },
  { icon: '↗', title: 'CPV & automated APQR', desc: 'Continued Process Verification and one-click APQR, built from live batch history.', color: '#00C9B1' },
  { icon: '⧈', title: 'Batch genealogy', desc: 'Forward and backward genealogy for impact assessment and root-cause.', color: '#F5A623' },
  { icon: '⛓', title: 'Recommendations', desc: 'Historical similarity surfaces likely root cause and CAPA for reviewers to weigh.', color: '#00B894' },
  { icon: '↻', title: 'Continuous learning', desc: 'Offline, change-controlled improvement from every run — never live-tuning a decision.', color: '#9B59B6', badge: 'Governed' },
];

function FeatureGrid({ items }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: 16,
    }}>
      {items.map((f, i) => {
        const [ref, inView] = useInView();
        return (
          <div key={f.title} ref={ref} style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 14, padding: '22px 22px 24px',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: `opacity 0.5s ease ${i * 70}ms, transform 0.5s ease ${i * 70}ms`,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = f.color + '30'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 9,
              background: f.color + '18',
              border: `1px solid ${f.color}25`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, color: f.color, marginBottom: 14,
            }}>{f.icon}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{f.title}</h4>
              {f.badge && (
                <span style={{
                  fontSize: 9, fontWeight: 800, letterSpacing: '0.06em',
                  background: f.badge === 'AI' ? '#00C9B1' : f.badge === 'Governed' ? '#9B59B6' : '#F5A623',
                  color: '#000', padding: '2px 7px', borderRadius: 100, textTransform: 'uppercase',
                }}>{f.badge}</span>
              )}
            </div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>{f.desc}</p>
          </div>
        );
      })}
    </div>
  );
}

function SectionHeader({ eyebrow, title, sub, color = '#00C9B1' }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      marginBottom: 56,
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
    }}>
      <p style={{
        fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
        color, marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
      }}>{eyebrow}</p>
      <h2 style={{
        fontSize: 'clamp(28px, 4.5vw, 50px)', fontWeight: 900, lineHeight: 1.1,
        letterSpacing: '-0.03em', color: '#fff', marginBottom: 16,
      }}>{title}</h2>
      <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', maxWidth: 540, lineHeight: 1.7 }}>{sub}</p>
    </div>
  );
}

export default function Features() {
  return (
    <section id="features" style={{ padding: '120px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* MBR */}
        <SectionHeader
          eyebrow="Master Batch Record"
          title="Author once. Automate the legacy."
          sub="No-code MBR authoring, supercharged by AI vision that structures scanned, typeset, and legacy BPRs."
          color="#7B3FE4"
        />
        <FeatureGrid items={mbrFeatures} />

        {/* Governed AI note */}
        <div style={{
          margin: '40px 0 80px',
          background: 'rgba(0,201,177,0.05)',
          border: '1px solid rgba(0,201,177,0.15)',
          borderLeft: '3px solid #00C9B1',
          borderRadius: '0 12px 12px 0',
          padding: '16px 24px',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#00C9B1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Governed AI</span>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginTop: 6, lineHeight: 1.6 }}>
            AI vision turns paper and legacy PDFs into governed, structured master records in minutes — every row human-verified and e-signed before issue. <em style={{ color: 'rgba(255,255,255,0.35)' }}>AI extraction is non-authoritative: it never bypasses the human e-signed accuracy check.</em>
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', margin: '0 0 80px' }} />

        {/* Foresight */}
        <SectionHeader
          eyebrow="Foresight"
          title="Every batch enriches the model."
          sub="Trend prediction, SPC, CPV, APQR, and genealogy turn accumulated runs into early-warning foresight."
          color="#F5A623"
        />
        <FeatureGrid items={foresightFeatures} />
        <div style={{
          margin: '40px 0 0',
          background: 'rgba(245,166,35,0.05)',
          border: '1px solid rgba(245,166,35,0.15)',
          borderLeft: '3px solid #F5A623',
          borderRadius: '0 12px 12px 0',
          padding: '16px 24px',
        }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
            All analytics are decision support; the deterministic engine and human e-signatures remain the only authorities.
          </p>
        </div>
      </div>
    </section>
  );
}
